import { useEffect, useState } from 'react';
import type { ClassMemberRow } from './classMembersStorage';
import { useMemberPhotoDisplayUrl } from './memberPhotoImageCache';
import {
  MEMBER_PROFILE_DISPLAY_MODE_CHANGED_EVENT,
  parseMemberProfileStorageImageRef,
  readMemberProfileDisplayMode,
  resolveMemberProfileSource,
  type MemberProfileDisplayMode,
} from './memberProfileDisplay';

/** workspace·grade 단위 사진 프로필 표시 모드 */
export function useMemberProfileDisplayMode(
  workspaceId: string,
  gradeId: string
): MemberProfileDisplayMode {
  const [mode, setMode] = useState<MemberProfileDisplayMode>(() =>
    readMemberProfileDisplayMode(workspaceId, gradeId)
  );

  useEffect(() => {
    setMode(readMemberProfileDisplayMode(workspaceId, gradeId));
    const onMode = (e: Event) => {
      const d = (e as CustomEvent<{ workspaceId?: string; gradeId?: string; mode?: MemberProfileDisplayMode }>)
        .detail;
      if ((d?.workspaceId ?? '') !== workspaceId || (d?.gradeId ?? '') !== gradeId) return;
      if (d?.mode === 'photo' || d?.mode === 'svg') setMode(d.mode);
    };
    window.addEventListener(MEMBER_PROFILE_DISPLAY_MODE_CHANGED_EVENT, onMode as EventListener);
    return () => {
      window.removeEventListener(MEMBER_PROFILE_DISPLAY_MODE_CHANGED_EVENT, onMode as EventListener);
    };
  }, [workspaceId, gradeId]);

  return mode;
}

/** 표시 모드에 따라 SVG ID 또는 사진 Blob/signed URL(비공개 버킷) */
export function useResolvedMemberProfileSrc(
  member: Pick<ClassMemberRow, 'profileImage' | 'photoProfilePath' | 'photoProfileVersion'> | null | undefined,
  workspaceId: string,
  gradeId: string
): string {
  const mode = useMemberProfileDisplayMode(workspaceId, gradeId);
  const wantsPhoto = mode === 'photo' && !!member?.photoProfilePath;
  const cardStoragePath = parseMemberProfileStorageImageRef(member?.profileImage);
  const displayUrl = useMemberPhotoDisplayUrl(
    wantsPhoto ? member?.photoProfilePath : cardStoragePath,
    wantsPhoto ? member?.photoProfileVersion : undefined
  );

  if (!member) return 'basic-0';
  // 사진 모드여도 URL이 아직 없으면(또는 실패) 기본/선택 SVG·프로필 카드로 폴백
  if (wantsPhoto) {
    return displayUrl || resolveMemberProfileSource(member);
  }
  if (cardStoragePath) {
    return displayUrl || resolveMemberProfileSource(member);
  }
  return resolveMemberProfileSource(member);
}
